/**
 * OnboardingIconChip: the only icon container in the wizard.
 *
 * A muted square, never tinted and never orange: 32px (`md`) or 24px (`sm`),
 * `rounded-md border border-border/40 bg-muted/40`, glyph 16px stroke 1.5 in
 * `text-muted-foreground`, `text-foreground` when selected. Tone signal in this
 * flow lives in the dot and the number, not in a colored glyph (DESIGN.md).
 *
 * Decorative by default (`aria-hidden`); pass `label` only when the chip is the
 * sole carrier of meaning, which makes it an `img` with an accessible name.
 */

import { HugeiconsIcon } from "@hugeicons/react"
import { cn } from "@/lib/utils"
import { onboardingIcon, type OnboardingIconKey } from "./icons"

type OnboardingIconChipProps = {
  icon: OnboardingIconKey
  /** Glyph switches to `text-foreground`. The chip itself never changes color. */
  selected?: boolean
  /** `md` = 32px chip / 16px glyph (default), `sm` = 24px chip / 14px glyph. */
  size?: "sm" | "md"
  /** Accessible name. Omit when adjacent text already says it. */
  label?: string
  className?: string
}

const CHIP_SIZE = {
  sm: "size-6",
  md: "size-8",
} as const

const GLYPH_SIZE = {
  sm: 14,
  md: 16,
} as const

export function OnboardingIconChip({
  icon,
  selected = false,
  size = "md",
  label,
  className,
}: OnboardingIconChipProps) {
  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md border border-border/40 bg-muted/40",
        CHIP_SIZE[size],
        className,
      )}
    >
      <HugeiconsIcon
        icon={onboardingIcon(icon)}
        size={GLYPH_SIZE[size]}
        strokeWidth={1.5}
        className={selected ? "text-foreground" : "text-muted-foreground"}
      />
    </span>
  )
}
